'use client';

import { useCallback } from 'react';
import { useLanguage } from '../hooks';

/**
 * Translation hook — supports nested dot-notation keys.
 *
 * Usage:
 *   const { t, language } = useTranslation();
 *   t('nav.home')           → "Home"
 *   t('login.otpSentTo', { phone: '9876543210' })
 *                            → "Enter the 6-digit code sent to +91 9876543210"
 */
export function useTranslation() {
  const { language, setLanguage, strings } = useLanguage();

  const t = useCallback(
    (key, vars) => {
      if (!key || typeof key !== 'string') return key ?? '';

      // Walk nested keys: "nav.home" → strings.nav.home
      const parts = key.split('.');
      let value = strings;
      for (const part of parts) {
        if (value == null || typeof value !== 'object') {
          value = undefined;
          break;
        }
        value = value[part];
      }

      // Fallback: return the last segment as readable text
      if (value === undefined || typeof value !== 'string') {
        return parts[parts.length - 1];
      }

      // Interpolate {{variable}} placeholders
      if (vars && typeof vars === 'object') {
        return value.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          vars[k] !== undefined ? String(vars[k]) : `{{${k}}}`
        );
      }

      return value;
    },
    [strings]
  );

  return { t, language, setLanguage };
}
